import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authService from '../../api/authService';
import {
  availablePages,
  buildActionPermissions,
  buildCustomPermissions
} from '../../constants/accessControl';

const createInitialForm = () => ({
  id: '',
  name: '',
  email: '',
  password: '',
  phone: '',
  role: 'staff',
  roleTemplateId: '',
  readOnly: false,
  allowedPages: ['customers'],
  actionPermissions: buildActionPermissions(false),
  customPermissions: buildCustomPermissions(false)
});

const allowedPageKeys = availablePages.map((page) => page.key);

const TeamMembersPage = () => {
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [formData, setFormData] = useState(createInitialForm());
  const [message, setMessage] = useState('');

  const isOwner = profile?.role === 'owner';
  const isEditingMember = !!formData.id;

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      const meRes = await authService.me();
      if (meRes.success) {
        setProfile(meRes.userInfo);
        if (meRes.userInfo?.role !== 'owner') {
          setMembers([]);
          setRoles([]);
          return;
        }
      }

      const membersRes = await authService.queryTeamMembers({
        page: 1,
        limit: 100,
        search,
        status
      });

      if (membersRes.success) {
        setMembers(membersRes.data || []);
        setRoles(membersRes.roles || []);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const resetForm = () => {
    setFormData(createInitialForm());
    setMessage('');
  };

  const applyRoleTemplate = (roleTemplateId) => {
    const selectedRole = roles.find((item) => item._id === roleTemplateId);
    if (!selectedRole) {
      setFormData((prev) => ({
        ...prev,
        roleTemplateId: '',
        readOnly: false
      }));
      return;
    }

    const nextAllowedPages = (selectedRole.allowedPages || []).filter((pageKey) => allowedPageKeys.includes(pageKey));

    setFormData((prev) => ({
      ...prev,
      roleTemplateId,
      readOnly: !!selectedRole.readOnly,
      allowedPages: nextAllowedPages.length ? nextAllowedPages : prev.allowedPages,
      actionPermissions: buildActionPermissions(!!selectedRole.readOnly, selectedRole.actionPermissions || {}),
      customPermissions: buildCustomPermissions(!!selectedRole.readOnly, selectedRole.customPermissions || {})
    }));
  };

  const handleEdit = (member) => {
    const nextAllowedPages = (member.allowedPages || []).filter((pageKey) => allowedPageKeys.includes(pageKey));

    setFormData({
      id: member._id,
      name: member.name || '',
      email: member.email || '',
      password: '',
      phone: member.phone || '',
      role: member.role || 'staff',
      roleTemplateId: member.roleTemplateId || '',
      readOnly: !!member.readOnly,
      allowedPages: nextAllowedPages.length ? nextAllowedPages : ['customers'],
      actionPermissions: buildActionPermissions(!!member.readOnly, member.actionPermissions || {}),
      customPermissions: buildCustomPermissions(!!member.readOnly, member.customPermissions || {})
    });
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (formData.allowedPages.length === 0) {
      setMessage('Please select at least one allowed page.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        roleTemplateId: formData.roleTemplateId || '',
        readOnly: formData.readOnly,
        allowedPages: formData.allowedPages,
        actionPermissions: formData.actionPermissions,
        customPermissions: formData.customPermissions
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      const res = formData.id
        ? await authService.updateTeamMember(formData.id, payload)
        : await authService.createTeamMember(payload);

      if (res.success) {
        setMessage(`Team member ${formData.id ? 'updated' : 'created'} successfully.`);
        resetForm();
        await loadPage();
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async (member) => {
    try {
      await authService.toggleTeamMemberStatus(member._id, { isActive: !member.isActive });
      await loadPage();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const allowedPageBadges = useMemo(
    () => availablePages.reduce((acc, page) => ({ ...acc, [page.key]: page.label }), {}),
    []
  );

  if (loading && !profile) {
    return <div className="p-5 text-center">Loading team members...</div>;
  }

  if (!isOwner) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning mb-0">Only the workspace owner can manage team members.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Team Members</h4>
          <p className="text-muted small mb-0">Create team logins, assign role templates, and fine-tune page and action access.</p>
        </div>
        <button className="btn btn-outline-secondary" onClick={resetForm}>New Member</button>
      </div>

      {message && (
        <div className={`alert ${message.toLowerCase().includes('success') || message.toLowerCase().includes('created') || message.toLowerCase().includes('updated') ? 'alert-success' : 'alert-info'}`}>
          {message}
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-700 mb-3">{formData.id ? 'Edit Team Member' : 'Add Team Member'}</h6>
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Role Label</label>
                    <select className="form-select" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                      <option value="manager">Manager</option>
                      <option value="staff">Staff</option>
                      <option value="support">Support</option>
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Role Template</label>
                    <select className="form-select" value={formData.roleTemplateId} onChange={(e) => applyRoleTemplate(e.target.value)}>
                      <option value="">Custom / No Template</option>
                      {roles.map((role) => (
                        <option key={role._id} value={role._id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{formData.id ? 'New Password (optional)' : 'Password'}</label>
                    <input className="form-control" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!formData.id} />
                  </div>
                </div>

                <div className="alert alert-light border mt-3 mb-0">
                  {isEditingMember
                    ? 'Edit mode keeps this simple: update the team member basic details and choose the role template. The role template controls page and permission access.'
                    : 'New team members also use the selected role template for access. No separate permission selection is needed here.'}
                </div>

                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (formData.id ? 'Update Member' : 'Create Member')}
                  </button>
                  {formData.id && (
                    <button type="button" className="btn btn-light" onClick={resetForm}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-8">
                  <input
                    className="form-control"
                    placeholder="Search by name, email, role"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Access</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">No team members found.</td>
                      </tr>
                    ) : members.map((member) => (
                      <tr key={member._id}>
                        <td>
                          <div className="fw-600">{member.name}</div>
                          <div className="small text-muted">{member.email}</div>
                        </td>
                        <td>
                          <div className="text-capitalize">{member.role}</div>
                          <div className="small text-muted">{member.roleTemplateName || 'Custom Role'}</div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(member.allowedPages || []).map((pageKey) => (
                              <span key={pageKey} className="badge bg-light text-dark border">
                                {allowedPageBadges[pageKey] || pageKey}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${member.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                            {member.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {member.readOnly && (
                            <div className="small text-muted mt-1">Read only</div>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-sm btn-light" onClick={() => handleEdit(member)}>Edit</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleStatusToggle(member)}>
                              {member.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMembersPage;
